import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, Component, Optional, Self, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@ng-bootstrap/ng-bootstrap';
import { NgbDateParserFormatter, NgbDatepickerI18nDefault, NgbDate, NgbDatepickerI18n, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { DATE_OPTIONS } from '@fgb/core';
import moment from 'moment/moment';

/**
 * Base class for date select components, contains main functionality for date select components to work
 */
class DateSelectBase {
    constructor(_ngControl) {
        this._ngControl = _ngControl;
        this._onChange = (val) => { };
        this._onTouched = () => { };
        this.placeholder = '--select--';
        this.readOnly = true;
        this.ariaLabel = 'Toggle date picker';
        if (this._ngControl) {
            this._ngControl.valueAccessor = this;
        }
        this.dateChange = new EventEmitter();
    }
    ngOnInit() {
        if (this._ngControl) {
            this.control = this._ngControl.control;
            this.control.updateValueAndValidity();
        }
    }
    writeValue(value) {
        this.innerValue = value;
        this._onChange(value);
        this.onDateChange(value);
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    onDateChange(date) {
        this.dateChange.emit({ date, isoString: this._getStringFromNgbDate(date) });
    }
    /** Gets a date string in ISO format from an NgbDate. */
    _getStringFromNgbDate(ngbDate) {
        if (ngbDate) {
            let date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, 12);
            date.setUTCHours(0);
            date.setUTCMinutes(0);
            date.setUTCSeconds(0);
            return date.toISOString();
        }
        else {
            return '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectBase, deps: [{ token: i1.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: DateSelectBase, selector: "dateSelectInput", inputs: { minDate: "minDate", maxDate: "maxDate", placeholder: "placeholder", readOnly: "readOnly", ariaLabel: "ariaLabel" }, outputs: { dateChange: "dateChange" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectBase, decorators: [{
            type: Directive,
            args: [{
                    selector: 'dateSelectInput' // For some reason library doesnt work without adding this
                }]
        }], ctorParameters: () => [{ type: i1.NgControl }], propDecorators: { minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], dateChange: [{
                type: Output
            }] } });

/**
 * Component for selecting a date input
 *
 * @example <fgbcl-date-select formControlName="anyControlName"></fgbcl-date-select>
 */
class DateSelectComponent extends DateSelectBase {
    constructor(ngControl) {
        super(ngControl);
        this.ngControl = ngControl;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectComponent, deps: [{ token: i1.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.1", type: DateSelectComponent, selector: "fgbcl-date-select", inputs: { label: "label" }, usesInheritance: true, ngImport: i0, template: "<div class=\"date-select\">\r\n  <div\r\n    class=\"input-wrapper form-floating\"\r\n    [ngClass]=\"{\r\n      touched: control?.touched,\r\n      invalid: !control?.valid\r\n    }\"\r\n  >\r\n    <input\r\n      type=\"text\"\r\n      class=\"form-control\"\r\n      [(ngModel)]=\"innerValue\"\r\n      [minDate]=\"minDate\"\r\n      [maxDate]=\"maxDate\"\r\n      ngbDatepicker\r\n      #datePicker=\"ngbDatepicker\"\r\n      (click)=\"datePicker.toggle()\"\r\n      (dateSelect)=\"writeValue($event)\"\r\n      [placeholder]=\"placeholder\"\r\n      [readonly]=\"readOnly\"\r\n      [disabled]=\"control?.disabled\"\r\n      autocomplete=\"off\"\r\n      (ngModelChange)=\"onDateChange($event)\"\r\n      id=\"date-select-input\"\r\n    />\r\n    <button\r\n      class=\"btn\"\r\n      (click)=\"datePicker.toggle()\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [disabled]=\"control?.disabled\"\r\n      id=\"date-select-button\"\r\n    >\r\n      <span class=\"material-icons align-middle\" aria-hidden=\"true\">calendar_month</span>\r\n    </button>\r\n    <label for=\"date-select-input\" *ngIf=\"label\">{{ label }}</label>\r\n  </div>\r\n</div>\r\n", styles: [":host{width:inherit}:host input:read-only{cursor:pointer}:host input:disabled{cursor:auto}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i3.NgbInputDatepicker, selector: "input[ngbDatepicker]", inputs: ["autoClose", "contentTemplate", "datepickerClass", "dayTemplate", "dayTemplateData", "displayMonths", "firstDayOfWeek", "footerTemplate", "markDisabled", "minDate", "maxDate", "navigation", "outsideDays", "placement", "popperOptions", "restoreFocus", "showWeekNumbers", "startDate", "container", "positionTarget", "weekdays", "disabled"], outputs: ["dateSelect", "navigate", "closed"], exportAs: ["ngbDatepicker"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fgbcl-date-select', template: "<div class=\"date-select\">\r\n  <div\r\n    class=\"input-wrapper form-floating\"\r\n    [ngClass]=\"{\r\n      touched: control?.touched,\r\n      invalid: !control?.valid\r\n    }\"\r\n  >\r\n    <input\r\n      type=\"text\"\r\n      class=\"form-control\"\r\n      [(ngModel)]=\"innerValue\"\r\n      [minDate]=\"minDate\"\r\n      [maxDate]=\"maxDate\"\r\n      ngbDatepicker\r\n      #datePicker=\"ngbDatepicker\"\r\n      (click)=\"datePicker.toggle()\"\r\n      (dateSelect)=\"writeValue($event)\"\r\n      [placeholder]=\"placeholder\"\r\n      [readonly]=\"readOnly\"\r\n      [disabled]=\"control?.disabled\"\r\n      autocomplete=\"off\"\r\n      (ngModelChange)=\"onDateChange($event)\"\r\n      id=\"date-select-input\"\r\n    />\r\n    <button\r\n      class=\"btn\"\r\n      (click)=\"datePicker.toggle()\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [disabled]=\"control?.disabled\"\r\n      id=\"date-select-button\"\r\n    >\r\n      <span class=\"material-icons align-middle\" aria-hidden=\"true\">calendar_month</span>\r\n    </button>\r\n    <label for=\"date-select-input\" *ngIf=\"label\">{{ label }}</label>\r\n  </div>\r\n</div>\r\n", styles: [":host{width:inherit}:host input:read-only{cursor:pointer}:host input:disabled{cursor:auto}\n"] }]
        }], ctorParameters: () => [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }], propDecorators: { label: [{
                type: Input
            }] } });

/**
 * Formats the date to not include the year
 */
class DateSelectNoYearFormatter extends NgbDateParserFormatter {
    constructor(dateOption) {
        super();
        this.dateOption = dateOption;
        this.dateFormat = 'MMM DD';
    }
    parse(value) {
        if (!value) {
            return null;
        }
        let date = moment(value, this.dateFormat);
        if (date.isValid()) {
            return { year: date.year(), month: date.month() + 1, day: date.date() };
        }
        return null;
    }
    format(date) {
        if (!date)
            return '';
        let mdt = moment([date.year, date.month - 1, date.day]);
        if (!mdt.isValid())
            return '';
        return mdt.format(this.dateFormat);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectNoYearFormatter, deps: [{ token: DATE_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectNoYearFormatter, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectNoYearFormatter, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DATE_OPTIONS]
                }] }] });

/**
 * Hides the year from the date picker
 */
class CustomDateI18n extends NgbDatepickerI18nDefault {
    getYearNumerals(year) {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CustomDateI18n, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CustomDateI18n }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CustomDateI18n, decorators: [{
            type: Injectable
        }] });

/**
 * Component for selecting a date without the year
 *
 * @example <fgbcl-date-select-no-year formControlName="anyControlName"></fgbcl-date-select-no-year>
 */
class DateSelectNoYearComponent extends DateSelectBase {
    constructor(ngControl) {
        super(ngControl);
        this.ngControl = ngControl;
    }
    ngOnInit() {
        if (this.innerValue && this.innerValue instanceof NgbDate) {
            // Limit the date range to the current year
            this.minDate = { year: this.innerValue.year, month: 1, day: 1 };
            this.maxDate = { year: this.innerValue.year, month: 12, day: 31 };
        }
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectNoYearComponent, deps: [{ token: i1.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.1", type: DateSelectNoYearComponent, selector: "fgbcl-date-select-no-year", inputs: { label: "label" }, providers: [
            {
                provide: NgbDatepickerI18n,
                useClass: CustomDateI18n
            },
            {
                provide: NgbDateParserFormatter,
                useExisting: DateSelectNoYearFormatter
            }
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"date-select\">\r\n  <div\r\n    class=\"input-wrapper form-floating\"\r\n    [ngClass]=\"{\r\n      touched: control?.touched,\r\n      invalid: !control?.valid\r\n    }\"\r\n  >\r\n    <input\r\n      type=\"text\"\r\n      class=\"form-control\"\r\n      [(ngModel)]=\"innerValue\"\r\n      [minDate]=\"minDate\"\r\n      [maxDate]=\"maxDate\"\r\n      ngbDatepicker\r\n      #datePicker=\"ngbDatepicker\"\r\n      (click)=\"datePicker.toggle()\"\r\n      (dateSelect)=\"writeValue($event)\"\r\n      [placeholder]=\"placeholder\"\r\n      [readonly]=\"readOnly\"\r\n      [disabled]=\"control?.disabled\"\r\n      autocomplete=\"off\"\r\n      (ngModelChange)=\"onDateChange($event)\"\r\n      id=\"date-select-input\"\r\n      outsideDays=\"hidden\"\r\n      navigation=\"arrow\"\r\n      [weekdays]=\"false\"\r\n    />\r\n    <button (click)=\"datePicker.toggle()\" [attr.aria-label]=\"ariaLabel\" [disabled]=\"control?.disabled\" id=\"date-select-button\">\r\n      <span class=\"material-icons align-middle\" aria-hidden=\"true\">calendar_month</span>\r\n    </button>\r\n    <label for=\"date-select-input\" *ngIf=\"label\">{{ label }}</label>\r\n  </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i3.NgbInputDatepicker, selector: "input[ngbDatepicker]", inputs: ["autoClose", "contentTemplate", "datepickerClass", "dayTemplate", "dayTemplateData", "displayMonths", "firstDayOfWeek", "footerTemplate", "markDisabled", "minDate", "maxDate", "navigation", "outsideDays", "placement", "popperOptions", "restoreFocus", "showWeekNumbers", "startDate", "container", "positionTarget", "weekdays", "disabled"], outputs: ["dateSelect", "navigate", "closed"], exportAs: ["ngbDatepicker"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectNoYearComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fgbcl-date-select-no-year', providers: [
                        {
                            provide: NgbDatepickerI18n,
                            useClass: CustomDateI18n
                        },
                        {
                            provide: NgbDateParserFormatter,
                            useExisting: DateSelectNoYearFormatter
                        }
                    ], template: "<div class=\"date-select\">\r\n  <div\r\n    class=\"input-wrapper form-floating\"\r\n    [ngClass]=\"{\r\n      touched: control?.touched,\r\n      invalid: !control?.valid\r\n    }\"\r\n  >\r\n    <input\r\n      type=\"text\"\r\n      class=\"form-control\"\r\n      [(ngModel)]=\"innerValue\"\r\n      [minDate]=\"minDate\"\r\n      [maxDate]=\"maxDate\"\r\n      ngbDatepicker\r\n      #datePicker=\"ngbDatepicker\"\r\n      (click)=\"datePicker.toggle()\"\r\n      (dateSelect)=\"writeValue($event)\"\r\n      [placeholder]=\"placeholder\"\r\n      [readonly]=\"readOnly\"\r\n      [disabled]=\"control?.disabled\"\r\n      autocomplete=\"off\"\r\n      (ngModelChange)=\"onDateChange($event)\"\r\n      id=\"date-select-input\"\r\n      outsideDays=\"hidden\"\r\n      navigation=\"arrow\"\r\n      [weekdays]=\"false\"\r\n    />\r\n    <button (click)=\"datePicker.toggle()\" [attr.aria-label]=\"ariaLabel\" [disabled]=\"control?.disabled\" id=\"date-select-button\">\r\n      <span class=\"material-icons align-middle\" aria-hidden=\"true\">calendar_month</span>\r\n    </button>\r\n    <label for=\"date-select-input\" *ngIf=\"label\">{{ label }}</label>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }], propDecorators: { label: [{
                type: Input
            }] } });

class DateSelectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: DateSelectModule, declarations: [DateSelectComponent,
            DateSelectNoYearComponent,
            DateSelectBase], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            NgbDatepickerModule], exports: [DateSelectComponent,
            DateSelectNoYearComponent,
            DateSelectBase] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            NgbDatepickerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DateSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DateSelectComponent,
                        DateSelectNoYearComponent,
                        DateSelectBase
                    ],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        NgbDatepickerModule
                    ],
                    exports: [
                        DateSelectComponent,
                        DateSelectNoYearComponent,
                        DateSelectBase
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CustomDateI18n, DateSelectBase, DateSelectComponent, DateSelectModule, DateSelectNoYearComponent, DateSelectNoYearFormatter };
